import '../styles/globals.css';
import type { AppProps } from 'next/app';
import Head from 'next/head';
import Script from 'next/script';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { Provider } from 'react-redux';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import initStore from '../store/store';
import Link from 'next/link';
import axios from 'axios';
import { ThemeProvider } from '../contexts/ThemeContext';
import SEO from '../components/SEO';
import ToastProvider from '../components/ToastProvider';
import { initWebVitals } from '../utils/webVitals';
import { trackPageView } from '../utils/analytics';
import setupAuthInterceptor from '../utils/authInterceptor';

const store = initStore();

// Shared client-side cache for API reads (previously every component fetched
// on every mount with no dedupe/caching). Conservative defaults so this only
// adds caching, it doesn't change what data shows: no background refetch on
// window focus, and callers that need it set their own staleTime/refetch.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

function MyApp({ Component, pageProps }: AppProps) {
  const router = useRouter();
  const routers = useRouter();
  // const [isLoggedIn, setIsLoggedIn] = useState(false);

  useEffect(() => {
    setupAuthInterceptor(router);
  }, [router]);

  // useEffect(() => {
  //   // Check if the auth token exists in localStorage
  //   const token = localStorage.getItem("auth-token");
  //   setIsLoggedIn(!!token); // Set isLoggedIn based on token presence
  // }, []);

  // const handleChatbotClick = (e: any) => {
  //   e.preventDefault(); // Prevent default link behavior

  //   if (!isLoggedIn) {
  //     router.push("/login"); // Redirect to login if not logged in
  //   } else {
  //     router.push("/supportchat"); // Otherwise, go to chatbot
  //   }
  // };

  useEffect(() => {
    const handleHashChange = () => {
      const { hash } = window.location;
      if (hash) {
        const target = document.querySelector(hash);
        if (target) {
          target.scrollIntoView({ behavior: 'smooth' });
        }
      }
    };

    router.events.on('hashChangeComplete', handleHashChange);
    return () => {
      router.events.off('hashChangeComplete', handleHashChange);
    };
  }, [router.events]);

  useEffect(() => {
    if (routers.pathname === '/supportchat') {
      localStorage.setItem('redirectTo', '/supportchat');
    } else if (routers.pathname !== '/login' && routers.pathname !== '/supportchat') {
      localStorage.removeItem('redirectTo');
    }
  }, [routers.pathname]);

  // Track page views on route change
  useEffect(() => {
    const handleRouteChange = (url: string) => {
      trackPageView(url);
    };

    router.events.on('routeChangeComplete', handleRouteChange);
    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
    };
  }, [router.events]);

  // Track initial page view only (the routeChangeComplete listener above
  // covers every navigation after that). This used to depend on
  // [router.asPath], so it re-ran on every navigation too - double-firing
  // trackPageView on every route change, not just the first load.
  useEffect(() => {
    trackPageView(router.asPath);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Initialize Web Vitals reporting
  useEffect(() => {
    initWebVitals();
  }, []);

  return (
    <>
      <Head>
        {/* Resource Hints for Core Web Vitals - LCP Optimization */}
        {/* Preconnect to external domains (Google Fonts removed - fonts are now self-hosted) */}
        <link rel="preconnect" href="https://api.vxfx.io" />
        <link rel="preconnect" href="https://vxfx-io.s3.eu-central-1.amazonaws.com" />
        <link rel="preconnect" href="https://www.googletagmanager.com" />
        <link rel="dns-prefetch" href="https://www.googletagmanager.com" />
        <link rel="dns-prefetch" href="https://www.google-analytics.com" />

        {/* Preload critical fonts */}
        <link
          rel="preload"
          href="/fonts/inter/Inter-Variable.woff2"
          as="font"
          type="font/woff2"
          crossOrigin="anonymous"
        />
        <link
          rel="preload"
          href="/fonts/sf-pro-woff2/SFPRODISPLAYREGULAR.woff2"
          as="font"
          type="font/woff2"
          crossOrigin="anonymous"
        />
        <link
          rel="preload"
          href="/fonts/sf-pro-woff2/SFPRODISPLAYMEDIUM.woff2"
          as="font"
          type="font/woff2"
          crossOrigin="anonymous"
        />

        {/* Favicon */}
        <link rel="shortcut icon" href="/epenfav.webp" />
        <link rel="icon" href="/epenfav.webp" />
        <link rel="apple-touch-icon" href="/epenfav.webp" />
        <meta
          name="google-site-verification"
          content="ih_rQoIJEP31zc5hVLWtNM7l1Xt5rH3qXey1dUbG3nk"
        />
        {/* Mobile Viewport Meta Tag - Ensures proper mobile rendering */}
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1, maximum-scale=5, user-scalable=yes"
        />
      </Head>
      <SEO />

      {/* Google Analytics - Deferred for better FID */}
      <Script
        src="https://www.googletagmanager.com/gtag/js?id=G-KVLS5RYWKC"
        strategy="lazyOnload"
      />
      <Script id="google-analytics" strategy="lazyOnload">
        {`
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          gtag('js', new Date());
          gtag('config', 'G-KVLS5RYWKC', {
            page_path: window.location.pathname,
            send_page_view: true
          });
        `}
      </Script>
      <QueryClientProvider client={queryClient}>
        <Provider store={store}>
          <ThemeProvider>
            <Component {...pageProps} />
          </ThemeProvider>
        </Provider>
      </QueryClientProvider>

      <div id="modals"></div>

      {/* Mounted once at the app root so toasts survive route changes
          (router.push / window.location.href right after a toast.success
          call used to unmount the page-local Toaster before it could
          render or persist across the navigation). */}
      <ToastProvider />
    </>
  );
}

export default MyApp;
